import { graphFetch } from "../../graph/client.ts";
import { writeFile } from "../../storage/fsa.ts";
import { shortHash } from "../../storage/hash.ts";
import { extractImageSrcs } from "./sanitize.ts";
import { pageAssetDiskPath, pageAssetRelativePath } from "./paths.ts";

/**
 * Fetches the images a OneNote page references (ARCHITECTURE.md §8: "images
 * need separate authenticated fetch").
 *
 * Non-image embedded objects (`<object data="...">` — a PDF or Word doc
 * dropped onto a page) are not fetched at all; the module map only calls out
 * images. The same kind of disclosed, not designed-away, gap as Teams' own
 * non-image attachment handling.
 */

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "image/gif": ".gif",
    "image/webp": ".webp",
    "image/bmp": ".bmp",
    "image/svg+xml": ".svg"
};

export interface FetchedPageImages {
    /** Raw src string (as found in the page's content HTML) -> path relative to the page's .html. */
    urlMap: Map<string, string>;
    bytes: number;
}

export async function fetchPageImages(params: {
    contentHtml: string;
    notebook: string;
    section: string;
    page: string;
    root: FileSystemDirectoryHandle;
    scopes: readonly string[];
    signal?: AbortSignal;
    onWarn: (message: string) => Promise<void>;
}): Promise<FetchedPageImages> {
    const { contentHtml, notebook, section, page, root, scopes, signal, onWarn } = params;
    const urlMap = new Map<string, string>();
    let bytes = 0;

    for (const src of extractImageSrcs(contentHtml)) {
        try {
            const response = await graphFetch(src, {
                scopes,
                service: "outlook",
                ...(signal ? { signal } : {})
            });
            const contentType = response.headers.get("Content-Type")?.split(";")[0]?.trim();
            const extension = (contentType && EXTENSION_BY_CONTENT_TYPE[contentType]) || ".bin";
            const hash = await shortHash(src, 16);

            const blob = await response.blob();
            const written = await writeFile(
                root,
                pageAssetDiskPath(notebook, section, page, hash, extension),
                blob
            );
            bytes += written;
            urlMap.set(src, pageAssetRelativePath(page, hash, extension));
        } catch (err) {
            // One missing image should not fail the whole page — the
            // sanitizer drops any <img> without a urlMap entry, so the page
            // still renders, just without that picture.
            const message = err instanceof Error ? err.message : String(err);
            await onWarn(`onenote image fetch failed: ${message}`);
        }
    }

    return { urlMap, bytes };
}
